import { Box, Typography, useMediaQuery } from "@mui/material";
import { alpha, type SxProps, type Theme } from "@mui/material/styles";
import { useEffect, useState } from "react";

/**
 * The centered, on-brand page loader that replaces full-page skeletons.
 *
 * A large witty line (a different one each load) over a modern, slim
 * brand-gradient indeterminate bar (navy → lime) that sweeps while data is
 * in flight. Deliberately mark-free — just the line + the bar, sized big so
 * the load reads as an intentional moment, not a spinner afterthought.
 *
 * The fade / sweep keyframes are global names defined in `globals.css`
 * (`xop-loader-*`); reduced-motion is honoured inline.
 *
 * Lives in `components/ui/` (lowercase file name — the case-sensitive
 * deploy box rule) so BOTH the dashboard and the CRM sub-app can import it.
 */

/** Creative loading copy — a different line each load. Short, warm, a touch
 *  playful; a few nod to XOP's "everything over packets". Exported for the
 *  unit test that pins the variety contract. */
export const LOADING_PHRASES: readonly string[] = [
  "Spinning up something awesome…",
  "Warming up the command center…",
  "Routing everything over packets…",
  "Reticulating splines…",
  "Herding a few stray electrons…",
  "Polishing the pixels…",
  "Summoning your dashboard…",
  "Aligning the constellations…",
  "Brewing something fresh…",
  "Untangling the wires…",
  "Charging the capacitors…",
  "Teaching the hamsters to run…",
  "Composing the view…",
  "Tuning the frequencies…",
  "Greasing the gears…",
  "Connecting the dots…",
  "Wrangling the data…",
  "Making it nice…",
  "Almost showtime…",
  "Calibrating the awesome…",
  "Loading something delightful…",
  "Plotting a course…",
  "Dusting off the good stuff…",
  "Assembling the pieces…",
  "Cueing the magic…",
  "Fetching fresh insights…",
  "Crunching the numbers…",
  "Pouring a fresh cup…",
  "Rolling out the red carpet…",
  "Shaking out the cobwebs…",
  "Counting the photons…",
  "Flipping the bits…",
  "Syncing the universe…",
  "Booting up the good vibes…",
  "Catching the fast packets…",
  "Putting on a show…",
  "Warming the tubes…",
  "Doing a little math…",
  "Lighting the runway…",
  "Stretching before the sprint…",
  "Loading the awesome sauce…",
  "Finding the magic words…",
  "Sharpening the pencils…",
  "Buffering brilliance…",
  "Lining up the dominoes…",
  "Priming the engines…",
];

/** A random phrase, never equal to `exclude` (so a cycle always advances). */
export function randomLoadingPhrase(exclude?: string): string {
  const pool = exclude
    ? LOADING_PHRASES.filter((p) => p !== exclude)
    : LOADING_PHRASES;
  const list = pool.length > 0 ? pool : LOADING_PHRASES;
  return list[Math.floor(Math.random() * list.length)] ?? LOADING_PHRASES[0]!;
}

export interface PageLoaderProps {
  /** Pin a fixed line (e.g. "Signing you in…") instead of the rotating copy. */
  message?: string;
  /** Fill the viewport (the initial app gate) rather than the content area. */
  fullScreen?: boolean;
  sx?: SxProps<Theme>;
}

const REDUCED = "@media (prefers-reduced-motion: reduce)";

export function PageLoader({
  message,
  fullScreen = false,
  sx,
}: PageLoaderProps): JSX.Element {
  const reduced = useMediaQuery(REDUCED, { noSsr: true });
  const [phrase, setPhrase] = useState(() => message ?? randomLoadingPhrase());

  // Rotate the copy every ~2.4s while loading — but only for the generic
  // (un-pinned) loader, and never under reduced-motion.
  useEffect(() => {
    if (message || reduced) return;
    const id = setInterval(
      () => setPhrase((cur) => randomLoadingPhrase(cur)),
      2400,
    );
    return () => clearInterval(id);
  }, [message, reduced]);

  useEffect(() => {
    if (message) setPhrase(message);
  }, [message]);

  return (
    <Box
      role="status"
      aria-live="polite"
      aria-busy="true"
      data-testid="page-loader"
      sx={[
        {
          display: "grid",
          placeItems: "center",
          // Pack the two rows (line + bar) to their content and center the
          // pair as a unit. Without an explicit `alignContent`, grid defaults
          // to `stretch`, which balloons each auto row to ~half the tall
          // container — stranding the line near the top and the bar far below
          // (the `gap` then reads as a huge void). `center` keeps them snug.
          alignContent: "center",
          gap: { xs: 1.5, sm: 2 },
          width: "100%",
          minHeight: fullScreen ? "100vh" : "min(60vh, 460px)",
          px: 3,
          textAlign: "center",
          ...(fullScreen
            ? {
                background: (t: Theme) =>
                  `radial-gradient(58% 50% at 50% 38%, ${alpha(t.palette.secondary.main, 0.1)} 0%, ${alpha(t.palette.primary.main, 0.06)} 38%, transparent 72%)`,
              }
            : {}),
        },
        ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
      ]}
    >
      {/* The witty line — large, in the display face. */}
      <Typography
        key={phrase}
        data-testid="page-loader-line"
        sx={{
          fontFamily: (t) => t.typography.h1.fontFamily, // DM Sans (display)
          fontSize: { xs: "1.6rem", sm: "2.1rem" },
          fontWeight: 600,
          lineHeight: 1.2,
          letterSpacing: "-0.015em",
          maxWidth: 560,
          color: "text.primary",
          animation: "xop-loader-fadeup 520ms cubic-bezier(0.22, 1, 0.3, 1) both",
          [REDUCED]: { animation: "none" },
        }}
      >
        {phrase}
      </Typography>

      {/* Modern indeterminate bar — the only indicator now. */}
      <Box
        aria-hidden
        data-testid="page-loader-bar"
        sx={{
          position: "relative",
          width: { xs: 240, sm: 320 },
          maxWidth: "80vw",
          height: 6,
          borderRadius: 999,
          overflow: "hidden",
          bgcolor: (t) => alpha(t.palette.primary.main, 0.12),
        }}
      >
        <Box
          sx={{
            position: "absolute",
            top: 0,
            bottom: 0,
            width: "40%",
            borderRadius: 999,
            background: (t) =>
              `linear-gradient(90deg, ${t.palette.primary.main}, ${t.palette.secondary.main})`,
            boxShadow: (t) => `0 0 12px ${alpha(t.palette.secondary.main, 0.5)}`,
            animation: "xop-loader-sweep 1.5s cubic-bezier(0.65, 0, 0.35, 1) infinite",
            [REDUCED]: { animation: "none", left: 0, width: "100%", opacity: 0.5 },
          }}
        />
      </Box>
    </Box>
  );
}
